import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

// Nöbet İzni Kuralları (AKM POC §6.5) — gün türü başına tutulan nöbetin kazandırdığı izin. Kredi puantaj
// nöbetin tutulduğunu gösterince doğar ve Ek İzin listesinde salt okunur görünür. Liste ucu SAYFASIZ dizi
// döner (kiracı başına en çok 3 etkin kural — gün türü başına bir).
export const DAY_TYPES = [
  { id: 0, labelKey: 'dutyLeaveRule.dayTypeWeekday' },
  { id: 1, labelKey: 'dutyLeaveRule.dayTypeWeekend' },
  { id: 2, labelKey: 'dutyLeaveRule.dayTypePublicHoliday' },
];

// DutyLeaveUnits: 0 saat, 1 gün.
export const UNITS = [
  { id: 0, labelKey: 'dutyLeaveRule.unitHours' },
  { id: 1, labelKey: 'dutyLeaveRule.unitDays' },
];

export const DEFAULT_HOURS_PER_LEAVE_DAY = 8;

const labelOf = (list, value, t) => {
  const item = list.find((x) => x.id === Number(value));
  return item ? t(item.labelKey) : '-';
};

const isBlank = (v) => v === '' || v === null || v === undefined;

// Sayı alanları TextInput'tan string gelir; virgüllü giriş ("1,5") de kabul edilir. Boş kullanım süresi
// = süresiz (null).
const toDecimal = (v) => Number(String(v ?? '').replace(',', '.'));

export const toDutyLeaveRulePayload = (formData) => {
  const fd = formData || {};
  const hours = toDecimal(fd.hoursPerLeaveDay);
  return {
    name: (fd.name || '').trim(),
    dayType: Number(fd.dayType ?? 0),
    earnedAmount: toDecimal(fd.earnedAmount),
    unit: Number(fd.unit ?? 0),
    leaveVariationId: fd.leaveVariationId || null,
    hoursPerLeaveDay: isBlank(fd.hoursPerLeaveDay) || Number.isNaN(hours) ? DEFAULT_HOURS_PER_LEAVE_DAY : hours,
    expiryDays: isBlank(fd.expiryDays) ? null : parseInt(fd.expiryDays, 10),
  };
};

// Yalnız saatlik OLMAYAN izin türleri: nöbet kredisi gün/saat olarak bakiyeye yazılır, saatlik türe
// bağlanamaz (sunucu: 400 dutyLeaveRule.leaveTypeHourly).
export const nonHourlyVariationOptions = (variations) => (variations || [])
  .filter((v) => !v.isHourly)
  .map((v) => ({ id: v.id, name: v.name }));

const useDutyLeaveRuleApi = () => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [variations, setVariations] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    setFetching(true);
    // api.js GET'te REDDETMEZ: hata sinyali res.data.status.
    api.get('/v1/duty-leave-rules')
      .then((res) => {
        if (res?.data?.status !== true) {
          setLoadFailed(true);
          setData([]);
          return;
        }
        setLoadFailed(false);
        const result = res.data.data;
        setData(Array.isArray(result) ? result : (result?.data || []));
      })
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  useEffect(() => {
    api.get('/v1/leave-variations').then((res) => {
      if (res?.data?.status !== true) return;
      const result = res.data.data;
      setVariations(Array.isArray(result) ? result : (result?.data || []));
    });
  }, []);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/duty-leave-rules', toDutyLeaveRulePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/duty-leave-rules/${id}`, toDutyLeaveRulePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (id) => {
    try {
      await api.delete(`/v1/duty-leave-rules/${id}`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const dayTypeOptions = useMemo(() => DAY_TYPES.map((d) => ({ id: d.id, name: t(d.labelKey) })), [t]);
  const unitOptions = useMemo(() => UNITS.map((u) => ({ id: u.id, name: t(u.labelKey) })), [t]);
  const variationOptions = useMemo(() => nonHourlyVariationOptions(variations), [variations]);

  // Sayfasız liste: istemci tarafı tablo; süzgeç/sıralama oku gösterilmez.
  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('dutyLeaveRule.name'), options: { filter: false, sort: false } },
    {
      name: 'dayType', label: t('dutyLeaveRule.dayType'),
      options: { filter: false, sort: false, customBodyRender: (v) => labelOf(DAY_TYPES, v, t) },
    },
    {
      name: 'earnedAmount', label: t('dutyLeaveRule.earnedAmount'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return `${v ?? '-'} ${labelOf(UNITS, row?.unit, t)}`;
        },
      },
    },
    {
      name: 'leaveVariationName', label: t('dutyLeaveRule.leaveType'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    { name: 'hoursPerLeaveDay', label: t('dutyLeaveRule.hoursPerLeaveDay'), options: { filter: false, sort: false } },
    {
      name: 'expiryDays', label: t('dutyLeaveRule.expiryDays'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ?? '-') },
    },
  ], [t, data]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('dutyLeaveRule.name'), type: 'text', grid: 12, required: true, maxLength: 100 },
    {
      name: 'dayType', label: t('dutyLeaveRule.dayType'), type: 'select', grid: 12, required: true,
      options: dayTypeOptions, idKey: 'id', labelKey: 'name', defaultValue: 0,
    },
    {
      name: 'earnedAmount', label: t('dutyLeaveRule.earnedAmount'), type: 'number', grid: 6, required: true,
      min: 0.01, max: 100, step: 0.01,
    },
    {
      name: 'unit', label: t('dutyLeaveRule.unit'), type: 'select', grid: 6, required: true,
      options: unitOptions, idKey: 'id', labelKey: 'name', defaultValue: 0,
    },
    {
      name: 'leaveVariationId', label: t('dutyLeaveRule.leaveType'), type: 'select', grid: 12, required: true,
      options: variationOptions, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'hoursPerLeaveDay', label: t('dutyLeaveRule.hoursPerLeaveDay'), type: 'number', grid: 6,
      min: 1, max: 24, step: 0.5, defaultValue: DEFAULT_HOURS_PER_LEAVE_DAY,
    },
    {
      name: 'expiryDays', label: t('dutyLeaveRule.expiryDays'), type: 'number', grid: 6,
      min: 1, max: 3650, step: 1, description: t('dutyLeaveRule.expiryHint'),
    },
  ], [t, dayTypeOptions, unitOptions, variationOptions]);

  return {
    objectSchema, columns, data, fetching, loadFailed, create, update, _delete,
  };
};

export default useDutyLeaveRuleApi;
